import { ChangeDetectorRef, Component, inject, OnInit } from "@angular/core";
import { Button, Header, HeaderTitle, Wrap } from "../../../components";
import { AppsIllustration } from "../../../components/AppsIllustration";
import { Arrows } from "../../../components/Arrows";
import { BackgroundGradient } from "../../../components/BackgroundGradient";
import { InstallFooter } from "../../../components/InstallFooter";
import { Integration } from "../../../components/Integration";
import { OrderFormComponent } from "../../../components/OrderForm";
import { AppIntegration } from "../../../components/DashboardIntegrations";
import { CombiPackage, User, UserCombiPackage } from "../../../model";
import { CombiPackageService } from "../../../services/combipackage/combipackage.service";
import { UserCombiPackageService } from "../../../services/usercombipackage/usercombipackage.service";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { UserService } from "../../../services/user/user.service";
import { Router } from "@angular/router";
import { UserCombiOrderSettings } from "../../../model/usercombiordersettings";
import { logBeginCheckout } from "../../../util/analytics.util";

@Component({
  standalone: true,
  selector: 'app-combipackageinstalled',
  imports: [
    AppsIllustration,
    Arrows,
    BackgroundGradient,
    Button,
    Header,
    HeaderTitle,
    InstallFooter,
    Integration,
    OrderFormComponent,
    TranslateModule,
    Wrap
  ],
  template: `
    <Header>
      <HeaderTitle class="center">
        {{ combiPackage.apiName }}
        <Arrows></Arrows>
        {{ combiPackage.otherApiName }}
        {{ 'PAGE_COMBIPACKAGEINSTALLED_TITLE_POSTFIX' | translate }}
      </HeaderTitle>
    </Header>
    <div class="pb-12 md:pb-16 lg:pb-24 bg-blue-50 flex flex-col mt-2.5 relative">
      <Wrap [class]="'w-full max-w-[780px] flex flex-col gap-7.5 -mt-10 md:-mt-16 lg:-mt-20 z-10 relative'">
        @if (showOrderBlock) {
          <div class="card">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm">🥳</div>
              <div class="flex-1">
                <h3 class="h3 mb-2">
                  {{ 'PAGE_COMBIPACKAGEINSTALLED_SUBTITLE_CONGRATS' | translate }}
                  {{ 'PAGE_COMBIPACKAGEINSTALLED_SUCCESS_TITLE' | translate }}.
                </h3>
                <div class="relative flex justify-center items-center mb-3">
                  <img src="assets/images/success.svg" class="w-1/2 h-auto" height="560" width="660"/>
                  <div class="absolute scale-50">
                    <AppsIllustration
                        [appName]="combiPackage.apiName"
                        [appIcon]="combiPackage.apiNameNormalized"
                        [secondAppName]="combiPackage.otherApiName"
                        [secondAppIcon]="combiPackage.otherApiNameNormalized"
                    ></AppsIllustration>
                  </div>
                </div>
                <p class="text-gray-600"
                   [translate]="'PAGE_COMBIPACKAGEINSTALLED_SUCCESS_BODY1'"
                   [translateParams]="{ fromApiName: combiPackage.apiName, toApiName: combiPackage.otherApiName }"></p>
              </div>
            </div>
          </div>
        } @else {
          <div class="card text-center relative md:px-15">
            <h2 class="h1 sm:absolute top-20 left-0 w-full sm:text-4xl" translate="PAGE_COMBIPACKAGEINSTALLED_SUBTITLE_CONGRATS"></h2>

            <div class="absolute left-0 w-full hidden xs:center top-30 sm:top-1/4">
              <AppsIllustration
                  [appName]="combiPackage.apiName"
                  [appIcon]="combiPackage.apiNameNormalized"
                  [secondAppName]="combiPackage.otherApiName"
                  [secondAppIcon]="combiPackage.otherApiNameNormalized"
              ></AppsIllustration>
            </div>

            <img
                [alt]="'PAGE_COMBIPACKAGEINSTALLED_SUBTITLE_SUCCESS' | translate"
                src="assets/images/success.svg"
                class="w-full h-auto mb-8"
                height="560"
                width="660"
            />

            <h3 class="h2" translate="PAGE_COMBIPACKAGEINSTALLED_SUCCESS_TITLE"></h3>
            <p translate="PAGE_COMBIPACKAGEINSTALLED_SUCCESS_BODY1" [translateParams]="{ fromApiName: combiPackage.apiName, toApiName: combiPackage.otherApiName }"></p>
            <p [innerHTML]="'PAGE_COMBIPACKAGEINSTALLED_SUCCESS_BODY2' | translate"></p>
          </div>
        }

        @if (showOrderBlock) {
          <div class="card">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold text-sm">🧠</div>
              <div class="flex-1">
                <h3 class="h3 mb-2" translate="PAGE_COMBIPACKAGEINSTALLED_ORDER_TITLE"></h3>
                <p [translate]="'PAGE_COMBIPACKAGEINSTALLED_ORDER_BODY'"
                   [translateParams]="{ daysLeft: userCombiPackage.combiOrder.daysLeft }"></p>

                @if (userCombiOrderSettings) {
                  <app-order-form
                      class="mt-4 block"
                      [user]="user"
                      [settings]="userCombiOrderSettings"
                      [combiOrder]="userCombiPackage.combiOrder"
                      [userCombiPackage]="userCombiPackage"
                      (orderSuccess)="router.navigate(['/install/success/confirmed'])">
                  </app-order-form>

                  <div class="mt-6 text-center">
                    <Button [color]="'blue'" [outline]="true" (click)="skip()">
                      {{ 'PAGE_COMBIPACKAGEINSTALLED_ORDER_SKIP' | translate }}
                    </Button>
                  </div>
                }
              </div>
            </div>
          </div>
        }

        @if (recommendedIntegrations) {
          <div id="save-more-time">
            <h3 translate="PAGE_COMBIPACKAGEINSTALLED_SAVEMORETIME_TITLE"></h3>
            <p translate="PAGE_COMBIPACKAGEINSTALLED_SAVEMORETIME_BODY" [translateParams]="{ fromApiName: combiPackage.apiName, toApiName: combiPackage.otherApiName }"></p>

            <div class="grid sm:grid-cols-2 lg:grid-cols-2 gap-5 mb-7.5">
              @for (integration of recommendedIntegrations; track integration.internalKey) {
                <Integration
                    [firstApp]="integration.from"
                    [secondApp]="integration.to"
                    [integration]="integration"
                ></Integration>
              }
            </div>

            <Button
                [color]="'blue'"
                [outline]="true"
                [href]="'/' + ('BREADCRUMBS_INTEGRATIONS_ROUTE' | translate) + '/' + combiPackage.apiName">
              + {{ 'PAGE_COMBIPACKAGEINSTALLED_BUTTON_MORE' | translate }}
            </Button>
          </div>
        }
      </Wrap>

      <BackgroundGradient/>
    </div>

    <InstallFooter/>
  `
})
export class CombiPackageInstalledComponent implements OnInit {
  combiPackage: CombiPackage;
  userCombiPackage: UserCombiPackage;
  user: User;
  userCombiOrderSettings: UserCombiOrderSettings;
  recommendedIntegrations: Array<AppIntegration>;

  // the page is filled in steps after async calls, each marking the view for check
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  constructor(private translate: TranslateService,
              private combiPackageService: CombiPackageService,
              private userCombiPackageService: UserCombiPackageService,
              private userService: UserService,
              readonly router: Router) {
  }

  get showOrderBlock(): boolean {
    return this.userCombiPackage?.combideskAppStorePackage === true
        && this.userCombiPackage?.status === 'TRIAL'
        && this.userCombiPackage?.combiOrder != null;
  }

  async ngOnInit() {
    this.combiPackage = this.userCombiPackageService.getCombiPackage();
    const storedPackage = this.userCombiPackageService.getUserCombiPackage();
    if (storedPackage?.id) {
      this.userCombiPackage = await this.userCombiPackageService.loadUserCombiPackage(storedPackage.id);
      this.changeDetectorRef.markForCheck();
    }

    if (this.showOrderBlock) {
      this.user = await this.userService.refreshUser();
      this.userCombiOrderSettings = {
        combiOrderID: this.userCombiPackage.combiOrder.id,
        name: this.user.name,
        companyName: this.user.companyName,
        street: this.user.street,
        zipcode: this.user.zipCode,
        city: this.user.city,
        telephone: this.user.telephoneNumber,
        invoiceEmail: this.user.invoiceEmail,
        vatNumber: this.user.vatNumber,
        period: 'YEAR',
        mollieRedirectUrl: 'install/success/confirmed'
      };
      logBeginCheckout(this.userCombiPackage.combiOrder.monthlyAmount, this.userCombiPackage);
      this.changeDetectorRef.markForCheck();
    }

    const recommendedCombiPackages = await this.combiPackageService.getRecommendedCombiPackagesForPackage(this.combiPackage.internalKey);
    this.recommendedIntegrations = recommendedCombiPackages
        .map(value => AppIntegration.fromCombiPackage(value, this.translate));
    this.changeDetectorRef.markForCheck();
  }

  skip(): void {
    this.router.navigate(['/dashboard']);
  }
}
